import React, { useEffect, useState, useMemo, useRef } from "react";
import { Box, Fade, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router";
import { useNavigationType } from "react-router-dom";
import { useSelector } from "react-redux";
import Appointment from "../../../Models/Appointment/Appointment";
import { CustomMainButton } from "../../common/components/CustomMainButton";
import { RequestLayout } from "../../common/components/RequestLayout";
import {
    sortServices,
    stepLayoutItemsId,
} from "../../../Utils";
import { StepInfo } from "../../common/components/StepInfo";
import { CommonLayout } from "../../common/components/CommonLayout";
import { useTranslation } from "react-i18next";
import { css } from "aphrodite";
import styles from "./styles";
import cancelAppointmentIcon from "../../assets/icons/cancelAppointmentIcon.svg";
import { RoutePath } from "../../../Utils/routePath";
import { Media } from "../../../Models";
import { CustomMobileServiceButton } from "../../common/components/CustomMobileServiceButton";
import cancelAppointmentMobileIcon from "../../assets/icons/cancelAppointmentMobileIcon.svg";
import theme from "../../common/theme/mellon";

const SelectServicePage = () => {
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const lastFetchedForBranchRef = useRef(null);
    const [isDataStale, setIsDataStale] = useState(false);
    const [t] = useTranslation(["translation"]);
    const {i18n} = useTranslation(["translation"]);
    const stepItems = useSelector(Appointment.getStepItems);
    const isLoading = useSelector(Appointment.getServicesLoading);
    const servicesData = useSelector(Appointment.getServicesData);
    const error = useSelector(Appointment.getServicesError);
    const selectedServiceId = useSelector(Appointment.getSelectedServiceId);
    const selectedBranchId = useSelector(Appointment.getSelectedBranchId);
    const isAppointmentByBranch = useSelector(Appointment.getIsAppointmentByBranch);
    const editAppointmentMode = useSelector(
        Appointment.getIsEditAppointmentMode
    );
    const waysOfActionIsLoading = useSelector(
        Appointment.getWaysOfActionLoading
    );
    const waysOfActionError = useSelector(Appointment.getWaysOfActionError);
    const waysOfActionData = useSelector(Appointment.getWaysOfActionData);
    const selectedServiceGroupId = useSelector(Appointment.getSelectedServiceGroupId);
    const selectedSubServiceGroupId = useSelector(Appointment.getSelectedSubServiceGroupId);
    const webView = useSelector(Media.getIsWebView);
    const [triggerGoToNextStep, setTriggerGoToNextStep] = useState(false);
    const currentStep = stepLayoutItemsId.selectService;
    useEffect(() => {
        // Only redirect on actual browser back (POP), not on mobile back button navigation (REPLACE)
        if (isAppointmentByBranch && !selectedBranchId && navigationType === 'POP') {
            navigate(RoutePath.selectBranchPage, { replace: true });
        }
    }, [selectedBranchId, isAppointmentByBranch, navigationType, navigate]);
    const services = useMemo(() => {
        if (Array.isArray(servicesData) && servicesData.length > 0) {
            const sortedData = servicesData.sort(sortServices)

            const individualServices = sortedData.filter(s => !s.tags?.ServiceGroupId && !s.tags?.SubServiceGroupId && !s.tags?.SubServiceId)

            const virtualServices = sortedData.filter(s => s.tags?.SubServiceId)

            const subServices = sortedData.filter(s => !s.tags?.ServiceGroupId && s.tags?.SubServiceGroupId && !s.tags?.SubServiceId)

            const serviceGroups = Object.groupBy(sortedData.filter(s => !!s.tags?.ServiceGroupId && !s.tags?.SubServiceGroupId), (s) => {
                return s.tags?.ServiceGroupId
            })
            const newServices = [
                ...individualServices,
                ...virtualServices,
                ...subServices,
                ...Object.keys(serviceGroups).map(key => serviceGroups[key][0]),
            ].sort(sortServices)
            return newServices
        }
        return []
    }, [servicesData]);
    useEffect(() => {
        if (isAppointmentByBranch && !selectedBranchId) {
            setIsDataStale(false);
            return;
        }
        const currentBranchKey = selectedBranchId || 'all';
        if (lastFetchedForBranchRef.current === currentBranchKey) {
            return;
        }
        if (servicesData && servicesData.length > 0 && lastFetchedForBranchRef.current !== currentBranchKey) {
            setIsDataStale(true);
        }
        lastFetchedForBranchRef.current = currentBranchKey;
        if (editAppointmentMode || selectedBranchId) {
            Appointment.fetchServices({
                body: {
                    BranchId: selectedBranchId,
                },
            });
        } else {
            Appointment.fetchServices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAppointmentByBranch, editAppointmentMode, selectedBranchId]);
    useEffect(() => {
        if (!isLoading && isDataStale) {
            setIsDataStale(false);
        }
    }, [isLoading, isDataStale]);

    useEffect(() => {
        if (triggerGoToNextStep) {
            if (selectedServiceGroupId) {
                Appointment.stepCompleted(currentStep);
                const currentStepIndex = stepItems.findIndex((step) => step.id === currentStep);
                const selectSubServiceStep = stepItems.find((step) => step.id === stepLayoutItemsId.selectSubService);
                Appointment.stepCompleted(selectSubServiceStep.id)
                let nextStep = stepItems[currentStepIndex + 2];
                setTriggerGoToNextStep(false);
                if (Array.isArray(waysOfActionData?.availableChannels)) {
                    Appointment.setSelectedChannel(
                        waysOfActionData.availableChannels[0]
                    );
                }
                navigate(nextStep.path);
            } else if (waysOfActionData?.availableChannels) {
                Appointment.setSelectedChannel(
                    waysOfActionData?.availableChannels[0]
                );
                Appointment.stepCompleted(currentStep);
                const currentStepIndex = stepItems.findIndex((step) => step.id === currentStep);
                const selectSubServiceStep = stepItems.find((step) => step.id === stepLayoutItemsId.selectSubService);
                Appointment.stepCompleted(selectSubServiceStep.id)
                let nextStep = stepItems[currentStepIndex + 2];
                setTriggerGoToNextStep(false);
                navigate(nextStep.path);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerGoToNextStep, waysOfActionData]);

    const handleOnClickService = async (serviceId, eventTypeId, serviceName, serviceDescription) => {
        Appointment.setSelectedService(serviceId, eventTypeId, serviceName, serviceDescription);
        const waysOfActionResponse = await Appointment.fetchWaysOfAction(eventTypeId);
        if (Array.isArray(waysOfActionResponse?.data?.availableChannels)) {
            Appointment.setSelectedChannel(waysOfActionResponse.data.availableChannels[0])
        }
        setTriggerGoToNextStep(true);
    };
    const handleOnClickServiceGroup = async (serviceGroupId) => {
        if (selectedBranchId) {
            const service = servicesData.find(s => s.tags?.ServiceGroupId === serviceGroupId)
            if (service) {
                Appointment.setSelectedServiceGroupId(serviceGroupId)
                Appointment.setSelectedService(service.id, service.eventTypeId, service.name, service.description);
                const waysOfActionResponse = await Appointment.fetchWaysOfAction(service.eventTypeId);
                if (Array.isArray(waysOfActionResponse?.data?.availableChannels)) {
                    Appointment.setSelectedChannel(waysOfActionResponse.data.availableChannels[0])
                }
                
                setTriggerGoToNextStep(true);
            }
        } else {
            Appointment.setSelectedServiceGroupId(serviceGroupId)
            setTriggerGoToNextStep(true);
        }
    }
    const handleOnClickSubService = (subServiceId) => {
        Appointment.stepCompleted(currentStep)
        Appointment.setSelectedSubServiceGroupId(subServiceId)
        navigate(RoutePath.selectSubServicePage);
    }

    const handleCancelAppointment = () => {
        navigate(RoutePath.cancelAppointmentPage);
    };

    return (
        <>
            <CommonLayout mainLayout={false}>
                <StepInfo
                    activeStepId={currentStep}
                    layoutItems={stepItems}
                    webView={webView}
                />
            </CommonLayout>
            <RequestLayout
                loading={
                    isLoading ||
                    isDataStale ||
                    // waysOfActionIsLoading ||
                    (triggerGoToNextStep && !waysOfActionError)
                }
                error={error || waysOfActionError}
            >
                {services.length > 0 && (
                    <>
                        <CommonLayout mainLayout={false}>
                            <Box sx={{ flexGrow: 1 }} className={css(editAppointmentMode && styles.bottomPadding)}>
                                <Grid container spacing={2} className={css(styles.gridDiv)}>
                                    {services.map((item, index) => {
                                        // Individual Services or Individual Sub Services.
                                        if ((!item.tags?.ServiceGroupId && !item.tags?.SubServiceGroupId && !item.tags?.SubServiceId && item.users?.length > 0) || (selectedSubServiceGroupId && !item.tags?.ServiceGroupId && item.users?.length > 0)) {
                                            return (
                                                <Grid className={css(styles.insideGrid)} key={item.id}>
                                                    {webView ? (
                                                        <CustomMainButton
                                                            isSelected={item.id === selectedServiceId}
                                                            textVariant="subtitle1"
                                                            value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                            onClick={() => handleOnClickService(
                                                                    item.id,
                                                                    item.eventTypeId,
                                                                    item.name,
                                                                    item.description
                                                            )}
                                                        />
                                                    ) : (
                                                        <CustomMobileServiceButton
                                                            isSelected={item.id === selectedServiceId}
                                                            value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                            onClickButton={() => handleOnClickService(
                                                                item.id,
                                                                item.eventTypeId,
                                                                item.name,
                                                                item.description
                                                            )}
                                                        />
                                                    )}
                                                </Grid>
                                            );
                                        // Service Groups or Sub Service Groups.
                                        } else if ((!!item.tags?.ServiceGroupId && !item.tags?.SubServiceGroupId) || (selectedSubServiceGroupId && !!item.tags?.ServiceGroupId)) {
                                            return (
                                                <Grid className={css(styles.insideGrid)} key={item.id}>
                                                    {webView ? (
                                                        <CustomMainButton
                                                            isSelected={selectedServiceGroupId === item.tags.ServiceGroupId}
                                                            textVariant="subtitle1"
                                                            value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                            onClick={() => handleOnClickServiceGroup(item.tags.ServiceGroupId)}
                                                        />
                                                    ) : (
                                                        <CustomMobileServiceButton
                                                            isSelected={selectedServiceGroupId === item.tags.ServiceGroupId}
                                                            value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                            onClickButton={() => handleOnClickServiceGroup(item.tags.ServiceGroupId)}
                                                        />
                                                    )}
                                                </Grid>
                                            )
                                        // Virtual Services when a sub service is not selected
                                        } else if (!!item.tags?.SubServiceId && !selectedSubServiceGroupId) {
                                            return (
                                                <Grid className={css(styles.insideGrid)} key={item.id}>
                                                    {webView ? (
                                                        <CustomMainButton
                                                            isSelected={selectedSubServiceGroupId === item.tags.SubServiceId}
                                                            textVariant="subtitle1"
                                                            value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                            onClick={() => handleOnClickSubService(item.tags.SubServiceId)}
                                                        />
                                                    ) : (
                                                        <CustomMobileServiceButton
                                                            isSelected={selectedSubServiceGroupId === item.tags.SubServiceId}
                                                            value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                            onClickButton={() => handleOnClickSubService(item.tags.SubServiceId)}
                                                        />
                                                    )}
                                                </Grid>
                                            )
                                        }
                                        return null;
                                    })}
                                </Grid>
                            </Box>
                        </CommonLayout>
                        <Fade appear={false} in={editAppointmentMode}>
                            <Paper
                                role="dialog"
                                square
                                variant="outlined"
                                sx={{
                                    position: "fixed",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    borderWidth: 0,
                                    borderTop: !webView
                                        ? `1px solid ${theme.palette.primary.dark}`
                                        : "0px",
                                }}
                            >
                                <CommonLayout mainLayout={false}>
                                    <div className={css(styles.titleRow)}>
                                        <img
                                            alt=""
                                            src={
                                                webView
                                                    ? cancelAppointmentIcon
                                                    : cancelAppointmentMobileIcon
                                            }
                                        />
                                        <Typography variant="h4">
                                            {t("Cancel Appointment")}
                                        </Typography>
                                    </div>
                                    <CustomMainButton
                                        fixedSize={false}
                                        color="error"
                                        style={styles.cancelButton}
                                        value={t("Cancel Appointment")}
                                        onClick={handleCancelAppointment}
                                    />
                                </CommonLayout>
                            </Paper>
                        </Fade>
                    </>
                )}
            </RequestLayout>
        </>
    );
};

export { SelectServicePage };
